import type {
  ErrorFeedbackType,
  FeedbackConfig,
  FeedbackType,
} from './FeedbackContent.types';

import { BadRequestIllustration } from './illustrations/BadRequestIllustration';
import { DataErrorIllustration } from './illustrations/DataErrorIllustration';
import { ForbiddenIllustration } from './illustrations/ForbiddenIllustration';
import { InternalServerErrorIllustration } from './illustrations/InternalServerErrorIllustration';
import { NoSearchResultsIllustration } from './illustrations/NoSearchResultsIllustration';
import { NotFoundIllustration } from './illustrations/NotFoundIllustration';
import { ServiceUnavailableIllustration } from './illustrations/ServiceUnavailableIllustration';
import { SessionExpiredIllustration } from './illustrations/SessionExpiredIllustration';
import { SoEmptyIllustration } from './illustrations/SoEmptyIllustration';
import { TimeoutIllustration } from './illustrations/TimeoutIllustration';

const ERROR_FEEDBACK_TYPES: Set<string> = new Set<string>([
  'bad-request',
  'auth-error',
  'forbidden',
  'not-found',
  'internal-server-error',
  'service-unavailable',
  'session-expired',
  'timeout',
]);

export function isErrorFeedbackType(
  type: FeedbackType
): type is ErrorFeedbackType {
  return ERROR_FEEDBACK_TYPES.has(type);
}

export const FEEDBACK_CONFIGS: Record<FeedbackType, FeedbackConfig> = {
  'auth-error': {
    title: "We couldn't verify your session.",
    message: 'Check your connection and try again.',
    Illustration: SessionExpiredIllustration,
  },
  'bad-request': {
    title: 'We all make mistakes!',
    message:
      'A bad request was sent and this page is now broken. We suggest you return to the safety of our homepage.',
    Illustration: BadRequestIllustration,
  },
  forbidden: {
    title: 'You wandered too far...',
    message:
      'Unfortunately, you don\u2019t have the permission to access this page. Let us escort you back to our homepage.',
    Illustration: ForbiddenIllustration,
  },
  'not-found': {
    title: 'This page seems to have missed its flight',
    message:
      'A missed flight is an opportunity to discover a new destination! Check out our homepage to explore your options.',
    Illustration: NotFoundIllustration,
  },
  'internal-server-error': {
    title: 'Our bad!',
    message:
      'Something went wrong on our servers. Try to refresh the page or contact us if the problem persists.',
    Illustration: InternalServerErrorIllustration,
  },
  'service-unavailable': {
    title: 'This page left on vacation',
    message:
      'This service is temporary unavailable due to maintenance downtime or capacity problems. Please try again later.',
    Illustration: ServiceUnavailableIllustration,
  },
  'session-expired': {
    title: 'Session expired.',
    message: 'You have been logged out due to inactivity.',
    Illustration: SessionExpiredIllustration,
  },
  timeout: {
    title: 'This page is late for work',
    message: 'The server request timed out! Try again in a few moments.',
    Illustration: TimeoutIllustration,
  },
  'data-error': {
    title: 'Error loading data',
    message: 'Looks like something went wrong.\nTry to refresh this page.',
    Illustration: DataErrorIllustration,
  },
  'so-empty': {
    title: 'So empty!',
    message: 'There is nothing to display at\nthe moment.',
    Illustration: SoEmptyIllustration,
  },
  'no-search-results': {
    title: 'No results found',
    message: 'Refine your search or adjust the\nfilters to see more results.',
    Illustration: NoSearchResultsIllustration,
  },
};
